import {
  type PayloadAction,
  createAsyncThunk,
  createSlice
} from '@reduxjs/toolkit';

import type {
  IFortePreliminaryLimitResult,
  IPreApprovalCurrentStatus
} from '@/api/api.types/pre-approval.api-types';
import type {
  IVehicleCity,
  IVehicleModel
} from '@/api/api.types/vehicles.api-types';
import { preApprovalApi } from '@/api/pre-approval.api';
import { vehiclesApi } from '@/api/vehicles.api';

import { setAuthStatus, setIsAuthorized, setUser } from './auth.slice';
import type { TRootState } from './index';

type TRequestStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

/** Ошибка запроса в том виде, в каком её бросает `ApiConnector.call`. */
export interface IPreApprovalRequestError {
  message: string;
  statusCode?: number;
}

interface IPreApprovalState {
  /** Владелец данных — при смене пользователя слайс сбрасывается. */
  ownerId: string | null;

  /** Снимок сервера. Клиент шаги не додумывает: после действий и в опросах перечитывает `current`. */
  server: {
    bootstrapStatus: TRequestStatus;
    current: IPreApprovalCurrentStatus | null;
    latestResult: IFortePreliminaryLimitResult | null;
  };

  /**
   * Токен клиента `current.client` — только в памяти, в хранилища не попадает.
   * ИИН, номер и город клиента отдаёт `current.client`, клиент их не хранит.
   */
  clientInfo: {
    clientToken: string | null;
  };

  /** Детали лимита `current.limitResultId` (сумма, срок): `current` их не отдаёт. */
  limit: {
    result: IFortePreliminaryLimitResult | null;
  };

  catalog: {
    cities: { status: TRequestStatus; data: IVehicleCity[] };
    models: {
      status: TRequestStatus;
      byCityId: Record<string, IVehicleModel[]>;
    };
  };
}

const initialState: IPreApprovalState = {
  ownerId: null,
  server: { bootstrapStatus: 'idle', current: null, latestResult: null },
  clientInfo: {
    clientToken: null
  },
  limit: { result: null },
  catalog: {
    cities: { status: 'idle', data: [] },
    models: { status: 'idle', byCityId: {} }
  }
};

const createPreApprovalThunk = createAsyncThunk.withTypes<{
  state: TRootState;
  rejectValue: IPreApprovalRequestError;
}>();

const toRequestError = (err: unknown): IPreApprovalRequestError => {
  const error = (err ?? {}) as { message?: unknown; statusCode?: unknown };
  return {
    message: typeof error.message === 'string' ? error.message : '',
    statusCode:
      typeof error.statusCode === 'number' ? error.statusCode : undefined
  };
};

/**
 * Первичная загрузка раздела: текущий процесс и последний валидный результат.
 * `null` в обоих ответах — норма (процесс не начат, результата нет), `rejected` — сеть или 5xx.
 * Отменённая загрузка (`abort`) ошибкой не помечается.
 */
export const bootstrapPreApproval = createPreApprovalThunk(
  'preApproval/bootstrap',
  async (_userId: string, { rejectWithValue }) => {
    try {
      const [current, latestResult] = await Promise.all([
        preApprovalApi.getCurrentStatus(),
        preApprovalApi.checkLatestResult()
      ]);
      return { current, latestResult };
    } catch (err) {
      return rejectWithValue(toRequestError(err));
    }
  }
);

/**
 * Перечитывает `current`: после действий клиента (код, биометрия, лимит) и в опросах ожидания.
 * Ошибка не трогает снимок — опрос просто повторится.
 */
export const refreshCurrent = createPreApprovalThunk(
  'preApproval/refreshCurrent',
  async (_: void, { getState, rejectWithValue }) => {
    const { ownerId } = getState().preApproval;
    try {
      return { ownerId, current: await preApprovalApi.getCurrentStatus() };
    } catch (err) {
      return rejectWithValue(toRequestError(err));
    }
  }
);

/**
 * clientToken для запросов с `preapproval-client-token`: из памяти или перевыпуск по аккаунту
 * через `current/client-token` (работает только после OTP). `force` — токен протух (401).
 */
export const ensureClientToken = createPreApprovalThunk(
  'preApproval/ensureClientToken',
  async (
    { force = false }: { force?: boolean } | undefined = {},
    { getState, rejectWithValue }
  ) => {
    const { ownerId, clientInfo, server } = getState().preApproval;
    // TODO(pre-approval-client-compat): `client` всегда есть — убрать `?.` у `client`
    const clientId = server.current?.client?.uuid;
    if (clientInfo.clientToken && clientId && !force) {
      return { ownerId, clientId, clientToken: clientInfo.clientToken };
    }
    try {
      const { clientId, accessToken } =
        await preApprovalApi.reissueClientToken();
      return { ownerId, clientId, clientToken: accessToken };
    } catch (err) {
      return rejectWithValue(toRequestError(err));
    }
  }
);

/** Справочник городов. Загружается один раз. */
export const fetchCities = createPreApprovalThunk(
  'preApproval/fetchCities',
  async (_: void, { rejectWithValue }) => {
    try {
      const cities = await vehiclesApi.getCities();
      // Города приходят в произвольном порядке — сортируем по алфавиту
      return [...cities].sort((a, b) => a.name.localeCompare(b.name));
    } catch (err) {
      return rejectWithValue(toRequestError(err));
    }
  },
  {
    condition: (_, { getState }) => {
      const { status } = getState().preApproval.catalog.cities;
      return status !== 'loading' && status !== 'succeeded';
    }
  }
);

/** Модели, доступные в городе. Кэшируются по городу. */
export const fetchModels = createPreApprovalThunk(
  'preApproval/fetchModels',
  async (cityId: string, { rejectWithValue }) => {
    try {
      return await vehiclesApi.getModels(cityId);
    } catch (err) {
      return rejectWithValue(toRequestError(err));
    }
  },
  {
    condition: (cityId, { getState }) =>
      !getState().preApproval.catalog.models.byCityId[cityId]
  }
);

/**
 * Кладёт свежий `current` в снимок. Токен сбрасывается, если сменился клиент (токен
 * выписан на `client.uuid`); детали лимита — если сменился сам лимит.
 */
const applyCurrent = (
  state: IPreApprovalState,
  current: IPreApprovalCurrentStatus | null
): void => {
  // TODO(pre-approval-client-compat): `client` всегда есть — убрать `?.` у `client`
  const previousClientId = state.server.current?.client?.uuid;
  const clientId = current?.client?.uuid;
  state.server.current = current;

  if (clientId !== previousClientId) {
    state.clientInfo = initialState.clientInfo;
  }
  if (state.limit.result?.id !== current?.limitResultId) {
    state.limit = initialState.limit;
  }
};

const preApprovalSlice = createSlice({
  name: 'preApproval',
  initialState,
  reducers: {
    setLatestResult: (
      state,
      action: PayloadAction<IFortePreliminaryLimitResult | null>
    ) => {
      state.server.latestResult = action.payload;
    },
    setClientToken: (state, action: PayloadAction<string>) => {
      state.clientInfo.clientToken = action.payload;
    },
    setLimitResult: (
      state,
      action: PayloadAction<IFortePreliminaryLimitResult>
    ) => {
      state.limit.result = action.payload;
    },
    resetPreApproval: () => initialState
  },
  extraReducers: (builder) => {
    builder
      .addCase(setIsAuthorized, (state, action) =>
        action.payload ? state : initialState
      )
      .addCase(setAuthStatus, (state, action) =>
        action.payload === 'authenticated' ? state : initialState
      )
      .addCase(setUser, (state, action) =>
        state.ownerId && action.payload?.id !== state.ownerId
          ? initialState
          : state
      )
      .addCase(bootstrapPreApproval.pending, (state, action) => {
        const userId = action.meta.arg;
        if (state.ownerId !== userId) {
          return {
            ...initialState,
            ownerId: userId,
            server: { ...initialState.server, bootstrapStatus: 'loading' }
          };
        }
        state.server.bootstrapStatus = 'loading';
      })
      .addCase(bootstrapPreApproval.fulfilled, (state, action) => {
        if (state.ownerId !== action.meta.arg) return;
        const { current, latestResult } = action.payload;
        state.server.bootstrapStatus = 'succeeded';
        state.server.latestResult = latestResult;
        applyCurrent(state, current);
      })
      .addCase(bootstrapPreApproval.rejected, (state, action) => {
        if (action.meta.aborted || state.ownerId !== action.meta.arg) return;
        state.server.bootstrapStatus = 'failed';
      })
      .addCase(refreshCurrent.fulfilled, (state, action) => {
        if (state.ownerId !== action.payload.ownerId) return;
        applyCurrent(state, action.payload.current);
      })
      .addCase(ensureClientToken.fulfilled, (state, action) => {
        if (state.ownerId !== action.payload.ownerId) return;
        state.clientInfo.clientToken = action.payload.clientToken;
      })
      .addCase(fetchCities.pending, (state) => {
        state.catalog.cities.status = 'loading';
      })
      .addCase(fetchCities.fulfilled, (state, action) => {
        state.catalog.cities = { status: 'succeeded', data: action.payload };
      })
      .addCase(fetchCities.rejected, (state) => {
        state.catalog.cities.status = 'failed';
      })
      .addCase(fetchModels.pending, (state) => {
        state.catalog.models.status = 'loading';
      })
      .addCase(fetchModels.fulfilled, (state, action) => {
        state.catalog.models.status = 'succeeded';
        state.catalog.models.byCityId[action.meta.arg] = action.payload;
      })
      .addCase(fetchModels.rejected, (state) => {
        state.catalog.models.status = 'failed';
      });
  }
});

export const {
  setLatestResult,
  setClientToken,
  setLimitResult,
  resetPreApproval
} = preApprovalSlice.actions;
export default preApprovalSlice;
